import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowUpCircle,
  Loader2,
  AlertCircle,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  CreditCard,
  MessageSquare,
  User,
  GraduationCap,
  ClipboardCheck,
  Briefcase,
  Award,
  Users,
} from "lucide-react";
import {
  useGetGradeUpgradeDetailsQuery,
  useUpgradeReopenMutation,
  useInitiateUpgradePaymentMutation,
  useGetUpgradeInductionQuery,
  useInitiateUpgradeInductionPaymentMutation,
} from "../api/upgradeApi";
import { useStartDuesPaymentMutation } from "../../billing/api/billingApi";
import { ceremonyFor, toGradeOf } from "../utils/ceremony";

/** Build and auto-submit the OnePay POST form (same flow as the wizard). */
function redirectToOnePay(res: any) {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = res.payment_url || "https://vuvaa.com/ONEPAY/mod/live/main";
  const fields: Record<string, any> = {
    merchant_reg_id: res.merchant_reg_id,
    amt_paid: res.amt_paid,
    merch_trans_id: res.merch_trans_id,
    client_phone: res.client_phone,
    product_desc: res.product_desc,
    client_email: res.client_email,
    client_name: res.client_name,
  };
  Object.entries(fields).forEach(([key, value]) => {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = key;
    input.value = String(value ?? "");
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
}

const STORAGE_BASE = "https://api.niqsng.org/storage";
const storageUrl = (p?: string | null) =>
  !p ? null : p.startsWith("http") ? p : `${STORAGE_BASE}/${p.replace(/^\//, "")}`;
// The uploaded certificate can arrive under a few different keys depending on
// the step — read them all so the "View" link never silently disappears.
const certOf = (row: any): string | null =>
  row?.cert_file ?? row?.certificate_path ?? row?.cert_url ?? row?.file ?? null;

const naira = (n?: number | null) =>
  n == null ? "—" : `₦${Number(n).toLocaleString("en-NG")}`;

function fmtDate(iso?: string | null) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? String(iso)
    : d.toLocaleString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
}

// Plain-language explanation of what each status means for the member.
const STATUS_HELP: Record<string, string> = {
  draft: "You haven't completed and paid for this application yet.",
  pending_payment: "Your application is complete — pay the upgrade fee to submit it.",
  submitted: "Your application has been submitted and is waiting for the secretariat to review it.",
  awaiting_endorsements:
    "The secretariat has reviewed your application. It is now waiting for your chapter's endorsement and your referees' acceptance before it goes to the committee for review.",
  under_review: "Your application is now with the committee for review.",
  // The ceremony is named per grade at the point of use — see statusHelp().
  recommended:
    "The Membership Committee has recommended your upgrade. Settle the fees below to complete it — your place is booked as soon as they are paid.",
  not_recommended:
    "The Membership Committee has not recommended your application at this stage. The committee chairman will confirm the outcome — there is nothing to pay in the meantime.",
  returned: "Your application was returned for editing — see what to fix below.",
  approved: "Congratulations — your upgrade has been approved.",
  rejected: "This application was not successful. The decision is final.",
};

/**
 * The status explanation, with the ceremony named for the grade being conferred.
 *
 * Only `recommended` differs: it is the one that talks about the event the fees
 * book a seat at, and a Fellow is invested rather than inducted.
 */
function statusHelp(status?: string, toGrade?: string | null): string | undefined {
  const key = String(status ?? "").toLowerCase();
  const text = STATUS_HELP[key];

  if (!text || key !== "recommended") return text;

  return text.replace(
    "your place is booked",
    `your place at the ${ceremonyFor(toGrade).eventNoun} is booked`,
  );
}

const STATUS_META: Record<
  string,
  { cls: string; icon: React.ReactNode; label: string }
> = {
  approved: { cls: "bg-green-100 text-green-700", icon: <CheckCircle className="w-3.5 h-3.5" />, label: "Approved" },
  rejected: { cls: "bg-red-100 text-red-700", icon: <XCircle className="w-3.5 h-3.5" />, label: "Rejected" },
  returned: { cls: "bg-amber-100 text-amber-700", icon: <AlertCircle className="w-3.5 h-3.5" />, label: "Returned for editing" },
  under_review: { cls: "bg-blue-100 text-blue-700", icon: <AlertCircle className="w-3.5 h-3.5" />, label: "Under review" },
  recommended: { cls: "bg-emerald-100 text-emerald-700", icon: <CheckCircle className="w-3.5 h-3.5" />, label: "Recommended — fees due" },
  not_recommended: { cls: "bg-orange-100 text-orange-700", icon: <AlertCircle className="w-3.5 h-3.5" />, label: "Not recommended" },
  awaiting_endorsements: { cls: "bg-violet-100 text-violet-700", icon: <AlertCircle className="w-3.5 h-3.5" />, label: "Reviewed — awaiting endorsements" },
  submitted: { cls: "bg-indigo-100 text-indigo-700", icon: <FileText className="w-3.5 h-3.5" />, label: "Submitted" },
  pending_payment: { cls: "bg-amber-100 text-amber-700", icon: <Clock className="w-3.5 h-3.5" />, label: "Awaiting payment" },
  draft: { cls: "bg-gray-100 text-gray-600", icon: <Clock className="w-3.5 h-3.5" />, label: "Draft — not paid" },
};

function StatusBadge({ status }: { status?: string }) {
  const s = (status ?? "draft").toLowerCase();
  const m = STATUS_META[s] ?? STATUS_META.draft;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold ${m.cls}`}>
      {m.icon}
      {m.label}
    </span>
  );
}

/**
 * The bill raised once the Membership Committee recommends the applicant.
 *
 * The lines are whatever the applicant's REVIEW CYCLE charges their grade —
 * upgrading charges, a conference fee, an investiture fee, a history book —
 * and the cycle sets them, so this card renders whatever arrives rather than
 * any fixed set. Applications in no cycle still fall back to the route-keyed
 * config schedule. It is one payment either way, it is compulsory in full, and
 * paying it books every event on it and approves the upgrade — there is no
 * further step, so the card says so plainly.
 *
 * Renders nothing at all when no such bill applies: any earlier status, or a
 * cycle/route nobody has priced yet.
 */
function InductionFeesCard({
  appId,
  status,
  toGrade,
}: {
  appId: string | number;
  status?: string;
  /** The grade being conferred — it decides whether this is an induction or an
   *  investiture, which is the only thing it changes here. See utils/ceremony. */
  toGrade?: string | null;
}) {
  const s = String(status ?? "").toLowerCase();
  const ceremony = ceremonyFor(toGrade);
  // Only fetch where a bill can exist — every other status would 402/return empty.
  const { data, isLoading } = useGetUpgradeInductionQuery(appId, {
    skip: !["recommended", "approved"].includes(s),
  });
  const [pay, { isLoading: starting }] = useInitiateUpgradeInductionPaymentMutation();
  // Clearing dues is a separate charge on a separate merchant, so it keeps its
  // own pending/error state rather than sharing the induction one.
  const [startDues, { isLoading: payingDues }] = useStartDuesPaymentMutation();
  const [error, setError] = useState("");
  const [duesError, setDuesError] = useState("");
  // One entry per extra guest card. Every field is required: each card becomes
  // a real event registration on confirmation, and one with nobody behind it
  // cannot be checked in at the gate or sent to its holder.
  type Guest = { name: string; email: string; phone: string };
  const [guests, setGuests] = useState<Guest[]>([]);

  if (isLoading || !data?.applies) return null;

  const paid = !!data.paid;
  // Dues owed. Nothing here is payable until they are cleared, so the card
  // stops asking for money and sends the member to settle those first.
  const arrears: number = Number(data.outstanding_balance ?? 0);
  const blockedByArrears = !paid && arrears > 0;

  const payDues = async () => {
    setDuesError("");
    try {
      const res: any = await startDues().unwrap();
      if (!res?.payment_url) throw new Error(res?.message || "Could not start the dues payment.");
      redirectToOnePay(res);
    } catch (e: any) {
      setDuesError(e?.data?.message ?? e?.message ?? "Could not start the dues payment. Please try again.");
    }
  };
  const cardFee: number = Number(data.guest_card?.amount ?? 0);
  const cardMax: number = Number(data.guest_card?.max ?? 0);
  const canBuyCards = !paid && data.payable && cardFee > 0;
  const guestTotal = guests.length * cardFee;
  const grandTotal = Number(data.total ?? 0) + guestTotal;

  // Caught here so an incomplete guest never costs a round trip to the gateway.
  const incompleteGuest = guests.findIndex(
    (g) => !g.name.trim() || !g.email.trim() || !g.phone.trim()
  );

  const startPayment = async () => {
    setError("");

    if (incompleteGuest > -1) {
      setError(
        `Guest ${incompleteGuest + 1}: enter a name, email and phone number. ` +
          "Each guest card is issued in their own name, so we need their details before you pay."
      );
      return;
    }

    try {
      const res: any = await pay({
        id: appId,
        guests: guests.map((g) => ({
          name: g.name.trim(),
          email: g.email.trim(),
          phone: g.phone.trim(),
        })),
      }).unwrap();
      if (res?.already_paid) { window.location.reload(); return; }
      if (!res?.payment_url) throw new Error(res?.message || "Online payment is unavailable right now.");
      redirectToOnePay(res);
    } catch (e: any) {
      setError(e?.data?.message ?? e?.message ?? "Could not start the payment. Please try again.");
    }
  };

  return (
    <div className={`rounded-2xl border p-5 shadow-sm ${paid ? "bg-green-50 border-green-200" : "bg-amber-50 border-amber-200"}`}>
      <div className="flex items-start gap-3">
        {paid
          ? <CheckCircle className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
          : <Clock className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />}
        <div className="min-w-0">
          <h3 className={`font-bold ${paid ? "text-green-900" : "text-amber-900"}`}>
            {ceremony.feesTitle(paid)}
          </h3>
          <p className={`text-sm mt-0.5 ${paid ? "text-green-800" : "text-amber-800"}`}>
            {paid
              ? `Your fees are settled and your place at the ${ceremony.eventNoun} is booked — see it under My Registrations.`
              : `The Membership Committee has recommended your upgrade. Paying these completes it and books your place at the ${ceremony.eventNoun}.`}
          </p>
        </div>
      </div>

      <ul className="mt-4 divide-y divide-black/5 rounded-xl bg-white/70 border">
        {(data.items ?? []).map((item: any) => (
          <li key={item.key} className="flex items-center justify-between gap-3 px-4 py-2.5">
            <span className="text-sm text-gray-700 min-w-0">
              {item.label}
              {item.event_title && (
                <span className="block text-xs text-gray-400 truncate">{item.event_title}</span>
              )}
            </span>
            <span className="text-sm font-semibold text-gray-900 shrink-0">{naira(item.amount)}</span>
          </li>
        ))}
        {guests.length > 0 && (
          <li className="flex items-center justify-between gap-3 px-4 py-2.5">
            <span className="text-sm text-gray-700">
              {guests.length} × additional guest card
              <span className="block text-xs text-gray-400">{naira(cardFee)} each</span>
            </span>
            <span className="text-sm font-semibold text-gray-900 shrink-0">{naira(guestTotal)}</span>
          </li>
        )}
        <li className="flex items-center justify-between gap-3 px-4 py-3 bg-white">
          <span className="text-sm font-bold text-gray-900">Total</span>
          <span className="text-base font-bold text-gray-900">{naira(grandTotal)}</span>
        </li>
      </ul>

      {/* Extra access cards for non-member guests. Any number, same payment. */}
      {canBuyCards && (
        <div className="mt-4 rounded-xl border bg-white/70 p-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-800">Bringing guests?</p>
              <p className="text-xs text-gray-500 mt-0.5">
                Additional access cards for non-members, {naira(cardFee)} each
                {data.guest_card?.event_title ? ` — ${data.guest_card.event_title}` : ""}.
                Add as many as you need. Each card is issued in the guest's own
                name, so give their full details.
              </p>
            </div>
            <button
              type="button"
              onClick={() =>
                setGuests((g) =>
                  g.length < cardMax ? [...g, { name: "", email: "", phone: "" }] : g
                )
              }
              disabled={guests.length >= cardMax}
              className="shrink-0 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-bold text-gray-700 hover:bg-gray-50 disabled:opacity-40"
            >
              + Add card
            </button>
          </div>

          {guests.length > 0 && (
            <div className="mt-3 space-y-2">
              {guests.map((g, i) => {
                const setField = (field: keyof Guest) => (e: any) =>
                  setGuests((list) =>
                    list.map((x, j) => (j === i ? { ...x, [field]: e.target.value } : x))
                  );

                return (
                  <div key={i} className="rounded-lg border bg-white p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[11px] font-bold uppercase tracking-widest text-gray-400">
                        Guest {i + 1} · {naira(cardFee)}
                      </span>
                      <button
                        type="button"
                        onClick={() => setGuests((list) => list.filter((_, j) => j !== i))}
                        className="rounded-lg border border-red-200 px-2.5 py-1 text-xs font-bold text-red-600 hover:bg-red-50"
                      >
                        Remove
                      </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <input
                        value={g.name}
                        onChange={setField("name")}
                        placeholder="Full name"
                        className="rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-100"
                      />
                      <input
                        type="email"
                        value={g.email}
                        onChange={setField("email")}
                        placeholder="Email address"
                        className="rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-100"
                      />
                      <input
                        value={g.phone}
                        onChange={setField("phone")}
                        placeholder="Phone number"
                        className="rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-100"
                      />
                    </div>
                  </div>
                );
              })}
              {guests.length >= cardMax && (
                <p className="text-[11px] text-gray-400">Maximum of {cardMax} guest cards per applicant.</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Cards already bought. Guests are recorded in their own right — they are
          not delegates of the event — so they are listed here rather than
          appearing under the member's registrations. */}
      {paid && (data.guests ?? []).length > 0 && (
        <div className="mt-4 rounded-xl border bg-white/70 p-4">
          <p className="text-sm font-semibold text-gray-800">
            Guest cards issued ({data.guests.length})
          </p>
          <ul className="mt-2 divide-y divide-black/5">
            {data.guests.map((g: any) => (
              <li key={g.id} className="py-2 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-gray-800 truncate">{g.name}</div>
                  <div className="text-xs text-gray-400 truncate">
                    {[g.email, g.phone].filter(Boolean).join(" · ")}
                  </div>
                </div>
                <span className="text-xs font-mono text-gray-400 shrink-0">
                  {String(g.access_code ?? "").slice(0, 8).toUpperCase()}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {blockedByArrears && (
        <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-4">
          <p className="text-sm font-bold text-rose-900">
            Clear your outstanding dues first
          </p>
          <p className="text-xs text-rose-800 mt-1 leading-relaxed">
            You owe <span className="font-bold">{naira(arrears)}</span> in dues. Your
            {" "}{ceremony.noun} fees cannot be paid until that is settled — your
            annual dues come before the upgrade.
          </p>

          {duesError && (
            <p className="mt-2 text-xs font-semibold text-rose-700">{duesError}</p>
          )}

          {/* Pays every outstanding dues invoice in one OnePay transaction —
              the member should not have to go hunting for them. */}
          <button
            onClick={payDues}
            disabled={payingDues}
            className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-rose-600 px-4 py-3 text-sm font-bold text-white hover:bg-rose-700 disabled:opacity-50 transition-colors"
          >
            {payingDues
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting to payment…</>
              : <>Pay {naira(arrears)} outstanding dues</>}
          </button>

          <Link
            to="/my-invoices"
            className="mt-2 block text-center text-xs font-semibold text-rose-700 hover:underline"
          >
            See what makes up this balance
          </Link>
        </div>
      )}

      {error && <p className="mt-3 text-sm font-semibold text-rose-700">{error}</p>}

      {!paid && data.payable && (
        <button
          onClick={startPayment}
          disabled={starting || incompleteGuest > -1}
          title={incompleteGuest > -1 ? "Complete every guest's details first" : undefined}
          className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gray-900 px-4 py-3 text-sm font-bold text-white hover:bg-gray-800 disabled:opacity-50 transition-colors"
        >
          {starting
            ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting to payment…</>
            : <>Pay {naira(grandTotal)}</>}
        </button>
      )}

      {/* Arrears have their own panel above, with the amount and a way to act
          on it — repeating the same sentence here would just say it twice. */}
      {!paid && !data.payable && !blockedByArrears && data.unavailable_reason && (
        <p className="mt-3 text-sm text-amber-800">{data.unavailable_reason}</p>
      )}
    </div>
  );
}

function Section({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white border rounded-xl overflow-hidden shadow-sm">
      <div className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2.5">
        {icon}
        <span className="text-sm font-semibold">{title}</span>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function Field({ label, value }: { label: string; value?: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-gray-400 font-semibold uppercase tracking-wide">{label}</span>
      <span className="text-sm text-gray-800">
        {value ?? <span className="text-gray-400 italic">—</span>}
      </span>
    </div>
  );
}

function Table({ heads, rows }: { heads: string[]; rows: React.ReactNode[][] }) {
  if (!rows || rows.length === 0) {
    return <p className="text-sm text-gray-400 italic">No entries recorded.</p>;
  }
  return (
    <div className="overflow-x-auto border rounded-lg">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-gray-400 uppercase border-b bg-gray-50">
            {heads.map((h) => <th key={h} className="px-3 py-2 font-semibold">{h}</th>)}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((r, i) => (
            <tr key={i}>{r.map((c, j) => <td key={j} className="px-3 py-2 text-gray-700 align-top">{c ?? "—"}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function subjectsText(subjects: any): string {
  if (!subjects || typeof subjects !== "object") return "—";
  return Object.entries(subjects).map(([k, v]) => `${k}: ${v}`).join(", ");
}

export default function GradeUpgradeDetailsPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { data: app, isLoading, isError, refetch } =
    useGetGradeUpgradeDetailsQuery(id, { skip: !id });
  const [reopen, reopenState] = useUpgradeReopenMutation();
  const [initiate, { isLoading: paying }] = useInitiateUpgradePaymentMutation();
  const [payErr, setPayErr] = useState<string | null>(null);
  const [blockers, setBlockers] = useState<string[]>([]);

  const handlePay = async () => {
    setPayErr(null);
    setBlockers([]);
    try {
      const res = await initiate(
        (app as any)?.route ? { route: (app as any).route } : undefined
      ).unwrap();
      if (!res?.merchant_reg_id || !res?.merch_trans_id) {
        setPayErr("Could not start payment. Please try again.");
        return;
      }
      redirectToOnePay(res);
    } catch (e: any) {
      const data = e?.data;
      if (Array.isArray(data?.blockers) && data.blockers.length) {
        setBlockers(
          data.blockers.map((b: any) =>
            typeof b === "string" ? b : b?.message ?? b?.label ?? b?.title ?? JSON.stringify(b)
          )
        );
      }
      setPayErr(data?.message || "Could not start payment. Please try again.");
    }
  };

  const handleReopen = async () => {
    try {
      await reopen({ id }).unwrap();
      // Reopened as a draft — the wizard resumes the current draft here.
      navigate("/upgrade-applications/grade");
    } catch (e: any) {
      alert(e?.data?.message || "Could not reopen this application. Please try again.");
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-3xl mx-auto p-6 py-24 flex flex-col items-center gap-3 text-gray-400">
        <Loader2 className="w-7 h-7 animate-spin" />
        <p className="text-sm">Loading your upgrade application…</p>
      </div>
    );
  }

  if (isError || !app) {
    return (
      <div className="max-w-3xl mx-auto p-6 py-20 text-center space-y-3">
        <AlertCircle className="w-8 h-8 text-red-400 mx-auto" />
        <p className="text-sm text-red-500">Could not load this upgrade application.</p>
        <div className="flex items-center justify-center gap-2">
          <button onClick={() => refetch()} className="px-4 py-2 rounded-lg border text-sm hover:bg-gray-50">
            Retry
          </button>
          <Link to="/upgrade-applications" className="px-4 py-2 rounded-lg border text-sm hover:bg-gray-50">
            Back
          </Link>
        </div>
      </div>
    );
  }

  const from = app.from_category?.name ?? "—";
  const to = app.to_category?.name ?? "Upgrade";
  const payment = app.payment;
  const invoice = app.invoice;

  const isPaid = String(payment?.status ?? "").toLowerCase() === "paid";
  // Payable only while the application is a draft / awaiting payment and unpaid.
  const canPay =
    !isPaid && ["draft", "pending_payment"].includes(String(app.status).toLowerCase());

  return (
    <div className="max-w-3xl mx-auto p-4 md:p-6 space-y-5">
      {/* Back */}
      <Link
        to="/upgrade-applications"
        className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800"
      >
        <ArrowLeft className="w-4 h-4" /> My upgrade applications
      </Link>

      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl border bg-gradient-to-br from-indigo-600 to-blue-700 text-white p-6 shadow-lg">
        <div className="absolute -top-10 -right-10 w-44 h-44 bg-white/10 rounded-full blur-2xl" />
        <div className="relative flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 text-indigo-100 text-xs font-semibold mb-1.5 bg-white/10 px-2.5 py-1 rounded-full">
              <ArrowUpCircle className="w-3.5 h-3.5" /> Grade Upgrade
            </div>
            <h1 className="text-2xl font-bold">
              {from} → {to}
            </h1>
            <p className="text-sm text-indigo-100 mt-1">
              Application #{String(app.id).padStart(6, "0")} · created {fmtDate(app.created_at)}
            </p>
          </div>
          <StatusBadge status={app.status} />
        </div>
        {app.route && (
          <span className="relative inline-block mt-3 px-2.5 py-1 rounded-lg bg-white/10 text-xs font-medium capitalize">
            {app.route} route
          </span>
        )}
      </div>

      {/* Post-recommendation fees — the only thing left between a recommended
          applicant and their upgrade, so it sits above everything else. */}
      <InductionFeesCard appId={app.id} status={app.status} toGrade={toGradeOf(app)} />

      {/* Review status / remark */}
      <Section icon={<MessageSquare className="w-4 h-4" />} title="Review">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Status" value={<StatusBadge status={app.status} />} />
          <Field label="Reviewed at" value={app.reviewed_at ? fmtDate(app.reviewed_at) : "Not yet reviewed"} />
        </div>
        {statusHelp(app.status, toGradeOf(app)) && (
          <p className="mt-3 text-sm text-gray-600">
            {statusHelp(app.status, toGradeOf(app))}
          </p>
        )}
        {/* The secretariat's note, and ONLY where it is addressed to the
            applicant: an application returned for editing, and the draft that
            reopening it produces. In every other state this column holds the
            Institute's internal reasoning — the chairman's note on finalising,
            or the justification an override demanded — which is not the
            applicant's to read. The server redacts it in those states too, so
            this condition is the second lock, not the only one. */}
        {app.remark && ["returned", "draft"].includes(String(app.status).toLowerCase()) && (
          <div className="mt-3 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-gray-700">
            <span className="text-xs font-semibold text-amber-600 uppercase block mb-1">
              What to fix
            </span>
            {app.remark}
          </div>
        )}
        {String(app.status).toLowerCase() === "returned" && (
          <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <p className="text-sm text-gray-600">
              Your application was returned for editing. Address the note above, then reopen
              it to fix the flagged details and resubmit — no new application or repeat payment is required.
            </p>
            <button
              onClick={handleReopen}
              disabled={reopenState.isLoading}
              className="shrink-0 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60"
            >
              {reopenState.isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Fix &amp; Resubmit
            </button>
          </div>
        )}
      </Section>

      {/* Payment */}
      <Section icon={<CreditCard className="w-4 h-4" />} title="Payment">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Upgrade fee" value={naira(app.amount != null ? Number(app.amount) / 100 : null)} />
          <Field
            label="Payment status"
            value={payment ? <StatusBadge status={payment.status === "paid" ? "approved" : payment.status} /> : "—"}
          />
          <Field label="Receipt number" value={payment?.receipt_number} />
          <Field label="Paid at" value={payment?.paid_at ? fmtDate(payment.paid_at) : "—"} />
          <Field label="Reference" value={app.reference} />
          <Field label="Gateway" value={payment?.gateway} />
        </div>

        {canPay && (
          <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex-1">
                <p className="text-sm font-semibold text-amber-900">
                  Payment required
                </p>
                <p className="text-xs text-amber-800 mt-0.5">
                  You haven't paid the upgrade fee for this application yet. Pay
                  now to submit it for review.
                </p>
              </div>
              <button
                onClick={handlePay}
                disabled={paying}
                className="shrink-0 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60"
              >
                {paying ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <CreditCard className="w-4 h-4" />
                )}
                {paying
                  ? "Starting payment…"
                  : `Pay upgrade fee${
                      app.amount != null ? ` · ${naira(Number(app.amount) / 100)}` : ""
                    }`}
              </button>
            </div>

            {payErr && (
              <p className="mt-3 text-sm text-red-600">{payErr}</p>
            )}
            {blockers.length > 0 && (
              <ul className="mt-2 list-disc list-inside space-y-1 text-xs text-red-600">
                {blockers.map((b, i) => (
                  <li key={i}>{b}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </Section>

      {/* Invoice & balance intentionally hidden from members */}

      {/* Personal details (step 1) */}
      <Section icon={<User className="w-4 h-4" />} title="Personal details">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Full name" value={[app.title, app.surname, app.other_names].filter(Boolean).join(" ") || "—"} />
          <Field label="Gender" value={app.gender} />
          <Field label="Date of birth" value={app.date_of_birth ? fmtDate(app.date_of_birth) : "—"} />
          <Field label="Nationality" value={app.nationality} />
          <Field label="Phone" value={app.phone} />
          <Field label="Email" value={app.email} />
          <Field label="State of origin" value={app.state?.name} />
          <Field label="L.G.A." value={app.lga?.name} />
          <Field label="Chapter" value={app.chapter?.name ?? app.chapter_other} />
          <Field label="Professional sector" value={app.sector?.name} />
          <Field label="Residential address" value={app.residential_address} />
          <Field label="Postal address" value={app.postal_address} />
          <Field label="Form completion" value={`Step ${app.completion_step ?? 0} of 8`} />
          <Field label="Acknowledged" value={app.acknowledged_at ? fmtDate(app.acknowledged_at) : "Not yet"} />
        </div>
      </Section>

      {/* Academic qualifications (step 2) */}
      <Section icon={<GraduationCap className="w-4 h-4" />} title="Academic qualifications">
        <Table
          heads={["Institution", "Qualification", "Year", "Certificate"]}
          rows={(app.qualifications ?? []).map((q: any) => [
            q.institution, q.qualification, q.year,
            storageUrl(certOf(q))
              ? <a className="text-indigo-600 hover:underline" href={storageUrl(certOf(q))!} target="_blank" rel="noreferrer">View</a>
              : "—",
          ])}
        />
      </Section>

      {/* O'level results (step 3) */}
      <Section icon={<ClipboardCheck className="w-4 h-4" />} title="O'level results">
        <Table
          heads={["Exam", "Year", "Exam No.", "Subjects", "Result"]}
          rows={(app.olevel_results ?? []).map((r: any) => [
            String(r.exam_type ?? "").toUpperCase(), r.exam_year, r.exam_number, subjectsText(r.subjects),
            storageUrl(r.result_file)
              ? <a className="text-indigo-600 hover:underline" href={storageUrl(r.result_file)!} target="_blank" rel="noreferrer">View</a>
              : "—",
          ])}
        />
      </Section>

      {/* Prelim / IJMB results (optional, step 3) */}
      {(app.prelim_results ?? []).length > 0 && (
        <Section icon={<ClipboardCheck className="w-4 h-4" />} title="Prelim / IJMB results">
          <Table
            heads={["Exam", "Year", "Institution", "Exam No.", "Subjects", "Result"]}
            rows={(app.prelim_results ?? []).map((p: any) => [
              p.exam_type === "others" ? (p.other_exam_name || "Others") : String(p.exam_type ?? "").toUpperCase(),
              p.exam_year, p.institution, p.exam_number, subjectsText(p.subjects),
              storageUrl(p.result_file)
                ? <a className="text-indigo-600 hover:underline" href={storageUrl(p.result_file)!} target="_blank" rel="noreferrer">View</a>
                : "—",
            ])}
          />
        </Section>
      )}

      {/* Professional memberships (step 4) */}
      <Section icon={<Award className="w-4 h-4" />} title="Professional memberships">
        <Table
          heads={["Body", "Stage passed", "Certificate"]}
          rows={(app.professional_memberships ?? []).map((m: any) => [m.body_name, m.stage_passed, m.certificate_path || "—"])}
        />
      </Section>

      {/* Work experience (step 5) */}
      <Section icon={<Briefcase className="w-4 h-4" />} title="Work experience">
        <Table
          heads={["Organization", "Position", "From", "To", "Responsibilities"]}
          rows={(app.experiences ?? []).map((x: any) => [
            x.organization, x.position,
            x.start_date ? fmtDate(x.start_date) : "—",
            x.end_date ? fmtDate(x.end_date) : "Present",
            x.responsibilities,
          ])}
        />
      </Section>

      {/* Seminars (step 6) */}
      <Section icon={<FileText className="w-4 h-4" />} title="Seminars & training">
        <Table
          heads={["Title", "Date", "Location"]}
          rows={(app.seminars ?? []).map((s: any) => [s.title, s.date ? fmtDate(s.date) : "—", s.location])}
        />
      </Section>

      {/* Referees (step 7) */}
      <Section icon={<Users className="w-4 h-4" />} title="Referees">
        <Table
          heads={["Referee", "Membership No.", "Relationship"]}
          rows={(app.referees ?? []).map((r: any) => [
            [r.member?.surname, r.member?.othernames].filter(Boolean).join(" ") || r.member_id,
            r.member?.membership_no,
            r.relationship,
          ])}
        />
      </Section>
    </div>
  );
}
